import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import accountApi from '../services/accountApi';
import bakeonatorLogo from '../assets/images/logos/bakeonator_logo.png';

const INVITE_STATE_KEY = 'bakeonator.invitation.continue';

function clearInviteState() {
  try {
    sessionStorage.removeItem(INVITE_STATE_KEY);
  } catch (err) {
    // ignore storage failures
  }
}

function storeInviteContinue(path) {
  try {
    sessionStorage.setItem(INVITE_STATE_KEY, path);
  } catch (err) {
    // ignore storage failures
  }
}

function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const { isAuthenticated, sessionLoading, refreshSession, setActiveTenant } = useAuth();

  const [validating, setValidating] = useState(Boolean(token));
  const [invitation, setInvitation] = useState(null);
  const [validateError, setValidateError] = useState(null);

  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState(null);
  const [accepted, setAccepted] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [registering, setRegistering] = useState(false);
  const [registerError, setRegisterError] = useState(null);
  const [fieldError, setFieldError] = useState(null);
  const [successSummary, setSuccessSummary] = useState(null);

  const returnPath = useMemo(() => {
    const params = new URLSearchParams();
    if (token) params.set('token', token);
    const query = params.toString();
    return query ? `/invitations/accept?${query}` : '/invitations/accept';
  }, [token]);

  const loginHref = `/login?returnTo=${encodeURIComponent(returnPath)}`;

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setValidateError('Invitation link is missing a token.');
      return;
    }
    let cancelled = false;
    (async () => {
      setValidating(true);
      setValidateError(null);
      setInvitation(null);
      try {
        const payload = await accountApi.validateInvitation(token);
        if (cancelled) return;
        if (!payload || !payload.valid) {
          setValidateError(
            (payload && payload.detail) || 'This invitation is invalid or no longer available.'
          );
          setInvitation(null);
        } else {
          setInvitation(payload);
        }
      } catch (err) {
        if (!cancelled) {
          setValidateError(err.message || 'Unable to validate invitation.');
        }
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  useEffect(() => {
    if (
      sessionLoading ||
      validating ||
      !isAuthenticated ||
      !token ||
      !invitation ||
      accepted ||
      accepting ||
      acceptError ||
      successSummary
    ) {
      return;
    }
    let cancelled = false;
    (async () => {
      setAccepting(true);
      setAcceptError(null);
      try {
        const membership = await accountApi.acceptInvitation(token);
        await refreshSession();
        if (membership && membership.tenant_id) {
          setActiveTenant(membership.tenant_id);
        }
        clearInviteState();
        if (!cancelled) {
          setAccepted(true);
          setSuccessSummary({
            tenantName: membership.tenant_name || invitation.tenant_name,
            role: membership.role || invitation.role,
            email: invitation.invited_email,
            created: false,
          });
        }
      } catch (err) {
        if (!cancelled) {
          setAcceptError(err.message || 'Unable to accept invitation');
        }
      } finally {
        if (!cancelled) setAccepting(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    sessionLoading,
    validating,
    isAuthenticated,
    token,
    invitation,
    accepted,
    accepting,
    acceptError,
    successSummary,
    refreshSession,
    setActiveTenant,
  ]);

  const handleRegister = async (event) => {
    event.preventDefault();
    setRegisterError(null);
    setFieldError(null);
    if (password !== passwordConfirm) {
      setFieldError('Passwords do not match.');
      return;
    }
    setRegistering(true);
    try {
      const result = await accountApi.registerFromInvitation({
        token,
        password,
        password_confirm: passwordConfirm,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });
      await refreshSession();
      const membership = result && result.membership;
      if (membership && membership.tenant_id) {
        setActiveTenant(membership.tenant_id);
      }
      clearInviteState();
      setAccepted(true);
      setSuccessSummary({
        tenantName: (membership && membership.tenant_name) || invitation.tenant_name,
        role: (membership && membership.role) || invitation.role,
        email: (result && result.email) || invitation.invited_email,
        created: true,
      });
    } catch (err) {
      const details =
        Array.isArray(err.details) && err.details.length ? err.details.join(' ') : null;
      setRegisterError(details || err.message || 'Unable to create account');
    } finally {
      setRegistering(false);
    }
  };

  const goToConsole = () => {
    clearInviteState();
    navigate('/console', { replace: true });
  };

  if (!token) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6">
            <Alert variant="danger">Invitation link is missing a token.</Alert>
            <Link to="/login">Sign in</Link>
          </div>
        </div>
      </div>
    );
  }

  if (validating || sessionLoading) {
    return (
      <div className="container mt-5 text-center" data-testid="invite-loading">
        <Spinner animation="border" size="sm" className="me-2" />
        Validating invitation…
      </div>
    );
  }

  if (validateError || !invitation) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <Alert variant="danger" data-testid="invite-invalid">
              {validateError || 'This invitation is invalid or no longer available.'}
            </Alert>
            <Link to="/login" className="me-3">
              Sign in
            </Link>
            <Link to="/">Back to home</Link>
          </div>
        </div>
      </div>
    );
  }

  if (successSummary) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="card shadow">
              <div className="card-body text-center">
                <img
                  src={bakeonatorLogo}
                  alt="Bakeonator Logo"
                  width="auto"
                  height="100"
                  className="mx-auto d-block mb-3"
                />
                <h2 className="card-title mb-3">
                  {successSummary.created ? 'Account created' : 'Invitation accepted'}
                </h2>
                <p className="text-muted mb-1">
                  You joined <strong>{successSummary.tenantName}</strong> as{' '}
                  <strong>{successSummary.role}</strong>.
                </p>
                {successSummary.email ? (
                  <p className="text-muted small">Signed in as {successSummary.email}</p>
                ) : null}
                <div className="d-grid mt-3">
                  <Button variant="primary" onClick={goToConsole}>
                    Continue to console
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            {acceptError ? (
              <>
                <Alert variant="danger">{acceptError}</Alert>
                <Button variant="outline-primary" onClick={() => setAcceptError(null)}>
                  Try again
                </Button>
              </>
            ) : (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Accepting invitation…
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (invitation.account_exists || invitation.sign_in_required) {
    return (
      <div className="container mt-5">
        <div className="row justify-content-center">
          <div className="col-md-6 col-lg-5">
            <div className="card shadow">
              <div className="card-body text-center">
                <img
                  src={bakeonatorLogo}
                  alt="Bakeonator Logo"
                  width="auto"
                  height="100"
                  className="mx-auto d-block mb-3"
                />
                <h2 className="card-title mb-3">Sign in to accept</h2>
                <p className="mb-1">
                  Join <strong>{invitation.tenant_name}</strong> as{' '}
                  <strong>{invitation.role_display_name || invitation.role}</strong>.
                </p>
                <p className="text-muted small mb-3">
                  Sign in with <strong>{invitation.invited_email}</strong> to continue.
                </p>
                <div className="d-grid gap-2">
                  <Link
                    to={loginHref}
                    className="btn btn-primary"
                    onClick={() => storeInviteContinue(returnPath)}
                  >
                    Sign in to accept invitation
                  </Link>
                  <Link to="/" className="btn btn-outline-secondary">
                    Back to home
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-5">
          <div className="card shadow">
            <div className="card-body">
              <img
                src={bakeonatorLogo}
                alt="Bakeonator Logo"
                width="auto"
                height="100"
                className="mx-auto d-block mb-3"
              />
              <h2 className="card-title text-center mb-2">Create your account</h2>
              <p className="text-muted text-center small mb-4">
                Join <strong>{invitation.tenant_name}</strong> as{' '}
                <strong>{invitation.role_display_name || invitation.role}</strong>.
              </p>
              {registerError ? <Alert variant="danger">{registerError}</Alert> : null}
              {fieldError ? <Alert variant="warning">{fieldError}</Alert> : null}
              <Form onSubmit={handleRegister} noValidate>
                <Form.Group className="mb-3" controlId="invite-email">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={invitation.invited_email || ''}
                    readOnly
                    plaintext={false}
                    data-testid="invite-email-readonly"
                  />
                  <Form.Text muted>Bound to this invitation; not editable.</Form.Text>
                </Form.Group>
                <Form.Group className="mb-3" controlId="invite-first-name">
                  <Form.Label>First name</Form.Label>
                  <Form.Control
                    type="text"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={registering}
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="invite-last-name">
                  <Form.Label>Last name</Form.Label>
                  <Form.Control
                    type="text"
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={registering}
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="invite-password">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={registering}
                    required
                  />
                  <Form.Text muted>
                    Use a strong password (Django password validators apply).
                  </Form.Text>
                </Form.Group>
                <Form.Group className="mb-3" controlId="invite-password-confirm">
                  <Form.Label>Confirm password</Form.Label>
                  <Form.Control
                    type="password"
                    autoComplete="new-password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    disabled={registering}
                    required
                  />
                </Form.Group>
                <div className="d-grid gap-2">
                  <Button type="submit" variant="primary" disabled={registering}>
                    {registering ? 'Creating account…' : 'Create account and join partner'}
                  </Button>
                  <Link to="/login" className="btn btn-outline-secondary">
                    Already have an account? Sign in
                  </Link>
                </div>
              </Form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AcceptInvitePage;
